import { usersHooks } from "api/client";
import { LoaderLogo } from "components/Loader";
import { useLinkableAccountsStatus } from "domains/auth/providers/Root/useLinkableAccountStatus";
import { forwardParams } from "domains/auth/queries/helpers";
import { useSession } from "domains/auth/queries/session";
import { authRoutes } from "domains/auth/views/Root/routes";
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { getAnalyticsUserTraits, identify, track } from "utils/analytics";
import { AuthContext, type IAuthContext } from "./context";
import { isExpectedError } from "./helpers";

export function AuthProvider() {
  const location = useLocation();
  const { hasSession } = useSession();
  if (!hasSession)
    return (
      <Navigate
        to={forwardParams(
          authRoutes.$buildPath({
            searchParams: { returnTo: `${location.pathname}${location.search}` },
          }),
        )}
      />
    );
  return <UserDataProvider />;
}

function UserDataProvider() {
  const { signOut } = useSession();
  const { data: user, refetch: refetchUser } = usersHooks.useAuthUser(undefined, {
    retry: (count, error) => (isExpectedError(error) ? false : count <= 1),
    staleTime: 5 * 60 * 100,
  });
  const {
    data: managerProfile,
    isLoading: profileLoading,
    refetch: refetchManagerProfile,
  } = usersHooks.useAuthManager(undefined, {
    retry: (count, error) => (isExpectedError(error) ? false : count < 1),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 100,
  });
  const analyticsUser = React.useMemo(() => {
    if (!user) return;
    return getAnalyticsUserTraits(user, managerProfile);
  }, [user, managerProfile]);

  React.useEffect(() => {
    if (analyticsUser) identify(analyticsUser.id, analyticsUser);
  }, [analyticsUser]);

  const loaded = !!user && !profileLoading;
  const signUpRequired = loaded && !(user?.app_mode && managerProfile?.id);
  const linkableAccountsStatus = useLinkableAccountsStatus({ signUpRequired });

  const globalLoading = !user || linkableAccountsStatus === "loading";
  const hasLocations = managerProfile?.has_locations;

  const contextValue: IAuthContext | undefined = React.useMemo(() => {
    if (globalLoading) return undefined;

    return {
      loaded,
      linkableAccountsStatus,
      signUpRequired,
      locationRequired: loaded && !hasLocations && !user.is_gigpro_admin,
      user,
      managerProfile,
      analyticsUser,
      hasLocations,
      logout: async () => {
        track("User Logout", {});
        await signOut();
      },
      refreshUser: async () => {
        const [user, managerProfile] = await Promise.all([refetchUser(), refetchManagerProfile()]);
        if (!(user.data && managerProfile.data)) return;
        return { user: user.data, managerProfile: managerProfile.data };
      },
    };
  }, [
    globalLoading,
    loaded,
    signUpRequired,
    linkableAccountsStatus,
    user,
    managerProfile,
    analyticsUser,
    hasLocations,
    refetchUser,
    refetchManagerProfile,
    signOut,
  ]);

  if (!contextValue) return <LoaderLogo />;

  return (
    <AuthContext.Provider value={contextValue}>
      <Outlet />
    </AuthContext.Provider>
  );
}
